import { Autocomplete, createFilterOptions, TextField } from "@mui/material";
import React, { useMemo } from "react";
import Box from "@mui/material/Box";
import { styled } from "@mui/material/styles";

const StyledTextField = styled(TextField)(({
    theme, label,
}) => ({
    "& .Mui-disabled fieldset": {
        backgroundColor: theme.palette.action.disabledBackground,
    },
    "& .Mui-disabled": {
        color: `${theme.palette.text.primary}!important`,
        WebkitTextFillColor: "initial",
    },
    "& .MuiOutlinedInput-root": {
        background: "none",
        fontSize: 12,
        margin: 0,
        padding: "0px !important",
        "& .MuiOutlinedInput-notchedOutline": {
            "&  legend": {
                border: "none",
            },
            top: 0,
            height: 24,
            ...label && {
                marginTop: 10,
            },
        },
        "& .MuiOutlinedInput-input": {
            padding: "4px 3px 2px 3px!important",
        },
        "& .MuiIconButton-root": {
            minWidth: 16,
            marginRight: 4,
        },
        ...label ? {
            marginTop: 4,
            "&>*:not(.MuiOutlinedInput-notchedOutline)": {
                marginTop: 12,
            },
        } : {
            "& .MuiSelect-iconOutlined": {
                marginTop: "1px!important",
            },
            "&>*:not(.MuiOutlinedInput-notchedOutline):not(.MuiSelect-select)": {
                marginTop: 2,
            },
            "& .MuiSelect-select": {
                marginTop: 0.75,
            },
        },
    },
    "& .MuiInputLabel-shrink": {
        fontSize: 12,
        transform: "translate(2px, -7px) scale(1) !important",
    },
    "& .MuiOutlinedInput-notchedOutline legend": {
        display: "none",
    },
    "& .MuiSelect-icon": {
        marginRight: -4,
    },
    "& .MuiAutocomplete-inputRoot:not(.Mui-disabled)": {
        paddingRight: "35px!important",
    },
    "& .MuiAutocomplete-inputRoot.Mui-disabled": {
        paddingRight: "10px!important",
    },
    "& .MuiAutocomplete-endAdornment button": {
        marginRight: "unset!important",
    },
    "& .MuiAutocomplete-endAdornment": {
        top: "unset!important",
        right: "0!important",
    },
    ...label ? {
        height: 36,
    } : {
        height: 24,
    },
    "&.text-field-root": {
        "& > div.MuiAutocomplete-inputRoot[class*='MuiOutlinedInput-root']": {
            paddingRight: "0px !important",

            "& > span": {
                order: 3,
                marginTop: "9px !important",
            },

            "& > button": {
                order: 3,
                marginTop: "3px !important",
                marginBottom: "-6px !important",
            },

            "& > div.MuiAutocomplete-endAdornment": {
                // marginTop: "4px !important",
                position: "relative",
                order: 2,
            },
            "& .MuiIconButton-root": {
                height: 15,
                width: 15,
            },
        },
    },
}));

const filter = createFilterOptions({
    // search entire Object, control this with prop if performance issues
    stringify: (option) => Object.keys(option).map((key) => option[key]).join(","),
});

export default function CustomAutocomplete(props) {
    const {
        fullWidth,
        disabled,
        required,
        className,
        label,
        name,
        value,
        onChange,
        options,
        optionsKey,
        optionsValueType,
        optionsName,
        optionsFormat,
        labelFormat,
        sx,
        inputRef,
        error,
        filterDisabled,
        filterData,
        addOption,
        disableClearable,
        freeSolo,
        startAdornment,
        endAdornment,
        blurOnSelect,
    } = props;

    return (
        useMemo(() => (
            <Autocomplete
                className={className}
                value={optionsValueType === "key" ? (options.find((option) => option[optionsKey] === value) || null) : value}
                inputValue={freeSolo && (value || "")}
                name={name}
                fullWidth={fullWidth}
                options={options}
                isOptionEqualToValue={(option, val) => (option[optionsKey] === val[optionsKey]) || null}
                onChange={(e, newValue, reason) => {
                    if (!freeSolo && (reason === "selectOption" || e.type === "click")) {
                        if (addOption && newValue?.inputValue) {
                            addOption(newValue);
                        }
                        if (optionsValueType === "key") {
                            onChange((newValue ? newValue[optionsKey] : null), (newValue || null), e);
                        } else {
                            onChange(newValue || null);
                        }
                    }
                }}
                onInputChange={(e, newValue) => {
                    if (freeSolo) {
                        let foundOption;

                        if (options && optionsValueType === "key") {
                            foundOption = options.find((item) => item[optionsKey] === newValue);
                        }
                        onChange(newValue, foundOption);
                    }
                }}
                clearOnEscape
                autoSelect={!freeSolo}
                disabled={disabled}
                {...disabled && { popupIcon: null }}
                filterOptions={(option, params) => {
                    const filtered = filter(option, params);
                    const isExisting = options.some((entry) => params.inputValue === entry[optionsName]);
                    if (params.inputValue !== "" && addOption && !isExisting) {
                        filtered.push({
                            [optionsName]: `"${params.inputValue}" hinzufügen`,
                            inputValue: params.inputValue,
                            id: Math.random().toString(36).substring(2, 5),
                        });
                    }
                    return filtered;
                }}
                includeInputInList
                renderInput={(params) => (
                    <StyledTextField
                        inputRef={inputRef}
                        {...params}
                        label={label}
                        required={required}
                        error={error}
                        {...freeSolo && { onChange }}
                        {...endAdornment && {
                            classes: {
                                root: "text-field-root",
                            },
                        }}
                        {...(endAdornment || startAdornment) && {
                            InputProps: {
                                ...params.InputProps,
                                startAdornment,
                                endAdornment: (
                                    <>
                                        {params.InputProps.endAdornment}
                                        {endAdornment}
                                    </>
                                ),
                            },
                        }}
                    />
                )}
                getOptionLabel={(option) => {
                    if (option.inputValue) {
                        return option.inputValue;
                    }
                    return labelFormat ? labelFormat(option) : option[optionsName] || "";
                }}
                getOptionDisabled={(option) => (filterDisabled ? filterDisabled(option, filterData) : false)}
                renderOption={(optionsProps, option) => (
                    <Box
                        component="li"
                        {...optionsProps}
                        key={option.id}
                        sx={{ fontSize: 12 }}
                    >
                        {(optionsFormat ? optionsFormat(option) : (labelFormat ? labelFormat(option) : option[optionsName])) || ""}
                    </Box>
                )}
                disableClearable={disableClearable}
                sx={sx}
                freeSolo={freeSolo}
                onKeyDown={(e) => {
                    if (freeSolo && e.key === "Tab" && value) {
                        const foundOptions = options
                            .map((option) => (optionsFormat ? optionsFormat(option) : (labelFormat ? labelFormat(option) : option[optionsName])) || "")
                            .filter((option) => option.substr(0, value.length).toLowerCase() === value.toLowerCase() && option !== value);

                        if (foundOptions.length > 0) {
                            onChange(foundOptions[0]);
                        }
                    }
                }}
                blurOnSelect={blurOnSelect}
            />
        ), [required, disabled, options, onChange, value]));
}
